export interface IdTokenClaims {
  iss: string;
  sub: string;
  aud: string | string[];
  exp: number;
  iat: number;
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
  picture?: string;
  /** Cognito username or preferred username */
  'cognito:username'?: string;
  /** Any additional claims */
  [key: string]: unknown;
}

export interface AuthUser {
  sub: string;
  email?: string;
  name?: string;
  picture?: string;
  expiresAt: number;
}

export type CallbackResult =
  | { type: 'success' }
  | { type: 'not_authorized' }
  | { type: 'error'; message: string };

export type ApiResult<T> =
  | { ok: true; status: number; data: T }
  | { ok: false; status: number; error: string };

export function isIdTokenClaims(value: unknown): value is IdTokenClaims {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;

  if (typeof v['iss'] !== 'string') return false;
  if (typeof v['sub'] !== 'string') return false;
  if (typeof v['exp'] !== 'number') return false;
  if (typeof v['iat'] !== 'number') return false;

  const aud = v['aud'];
  if (
    typeof aud !== 'string' &&
    !(Array.isArray(aud) && aud.every((a) => typeof a === 'string'))
  ) {
    return false;
  }

  if (v['email'] !== undefined && typeof v['email'] !== 'string') return false;
  if (v['name'] !== undefined && typeof v['name'] !== 'string') return false;
  if (v['picture'] !== undefined && typeof v['picture'] !== 'string') return false;
  if (v['email_verified'] !== undefined && typeof v['email_verified'] !== 'boolean') return false;

  return true;
}

export function toAuthUser(claims: IdTokenClaims): AuthUser {
  let name = claims.name;
  if (!name && (claims.given_name || claims.family_name)) {
    name = [claims.given_name, claims.family_name].filter(Boolean).join(' ');
  }
  return {
    sub: claims.sub,
    email: claims.email,
    name,
    picture: claims.picture,
    expiresAt: claims.exp,
  };
}
